import { Component, ElementRef, EventEmitter, Input, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatSort, Sort, SortDirection } from '@angular/material/sort';
import { MatLegacyRow as MatRow, MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { ClientContextService, CommonBrowserStorageService } from '@sdbn/appclient';
import { TranslationService } from '@sdbn/common';
import { Subject } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';
import { getTransportDetailById, TransportDetail, TransportDetailDefinition, TransportDetailUsage } from 'src/tmc-yard/common/configuration/transport.configuration';
import { DataType } from 'src/tmc-yard/common/model/common.model';
import { Transport, TransportStatus, TransportType } from 'src/tmc-yard/common/model/transport.model';
import { PendingTaskState } from 'src/tmc-yard/widgets/control/transport-list/model';
import { SortDefinition } from '../transport-list.model';

const TABLE_SORT_KEY = 'transport-overview.table.sort';

@Component({
  selector: 'app-transport-list',
  templateUrl: './transport-list.component.html',
  styleUrls: ['./transport-list.component.scss']
})
export class TransportListComponent {

  DataType = DataType;

  PendingTaskState = PendingTaskState;

  @Input('sortDefinition') set setSortDefinition(value: SortDefinition) {

    console.log('receiving sort definition', value);

    if (value != null) {
      this.sortDefinition = value;
      if (this.sort != null) {
        this.emitSort();
      }
    } else {
      this.initializeSort();
    }
  }

  @Input() columnsToDisplay = new Array<string>();

  @Input('transports') set setTransports(value: Array<Transport>) {

    this.transports = value;
    this.updateData();

  }

  @Input() componentIdentifier: string;

  @Input('selectedTransportId') set setTransportSelection(id: number) {
    if (id != null && id != undefined && id !== this.selectedId) {
      this.selectedId = id;
      this.requestScrollToSelection.next();
      this.manuallyScrolled = false;
    }
  }

  @Output() transportSelected = new EventEmitter<Transport>();

  sortDefinition: SortDefinition;

  /* hieraus werden die Columns im Template erzeugt */
  possibleColumns: Array<TransportDetail>;

  transports: Array<Transport>;

  dataSource = new MatTableDataSource(new Array<any>());

  selectedId: number;

  manuallyScrolled: boolean = false;

  currentTop = 0;
  autoScroll = false;

  requestScrollToSelection = new Subject<null>();

  @ViewChild(MatSort, { static: true }) sort: MatSort;

  @ViewChildren(MatRow, { read: ElementRef }) tableRows: QueryList<ElementRef>;

  constructor(
    public translationService: TranslationService,
    private commonBrowserStorageService: CommonBrowserStorageService,
    private clientContextService: ClientContextService
  ) {

    let columnsToDisplay = new Array<string>();
    let possibleColumns = new Array<TransportDetail>();
    columnsToDisplay.push('hint');
    for (const group of TransportDetailDefinition) {
      for (const detail of group.details) {
        if (detail.usage === TransportDetailUsage.BOTH || detail.usage === TransportDetailUsage.TABLE) {
          possibleColumns.push(detail);
          columnsToDisplay.push(detail.identifier);
        }
      }
    }
    this.columnsToDisplay = columnsToDisplay;
    this.possibleColumns = possibleColumns;

    translationService.languageChanged.subscribe(() => this.updateData());

  }

  private updateData(): void {

    if (this.transports == null) {
      return;
    }

    let manuallyScrolled = this.manuallyScrolled;
    this.dataSource.data = this.sortData(this.convert(this.transports), this.sort);

    (async () => {

      await new Promise(f => setTimeout(f, 1500));

      if (this.manuallyScrolled && !manuallyScrolled) {
        this.manuallyScrolled = false;
      }
      if (!this.manuallyScrolled && this.selectedId != null && this.selectedId != undefined) {
        this.requestScrollToSelection.next();
      }

    })();

  }

  private convert(transports: Array<Transport>): Array<any> {
    const result = new Array<any>();
    for (const transport of transports) {
      let object = { transport: transport, statusStyle: transport.statusStyle, highlightingStyle: transport.highlightingStyle, tableRowStyles: transport.tableRowStyles };
      for (let detail of this.possibleColumns) {
        object[detail.identifier] = transport.getValueByPath(detail.attribute);
      }
      result.push(object);
    }
    return result;
  }

  ngOnInit() {


    console.log('columns to display', this.columnsToDisplay);

    this.requestScrollToSelection.pipe(debounceTime(300), tap(() => this.scrollSelectionIntoView())).subscribe(() => { });

    this.dataSource.sortingDataAccessor = (item: any, property) => {
      return item[property];
    };

    // must be set in ngOnInit!
    this.dataSource.sort = this.sort;
    this.dataSource.sort.disableClear = true;

    /* nur wenn von außen bisher keine SortColumn gesetzt wurde */
    if (this.sortDefinition == null) {
      this.initializeSort();
    } else {
      this.emitSort();
    }

    this.dataSource.sortData = this.sortData.bind(this);

    this.requestScrollToSelection.next();
  }

  onClickRow(transport: Transport) {
    if (this.selectedId !== transport.id) {
      this.selectedId = transport.id;
      this.transportSelected.next(transport);
    }
  }



  public sortChanged(event) {
    this.commonBrowserStorageService.setPersistent(this.clientContextService.getUnit().key, this.componentIdentifier, TABLE_SORT_KEY, JSON.stringify({ sortColumn: event.active, sortDirection: event.direction }));
  }

  private restoreTableSort(): boolean {
    console.log('restoring sort');
    const tableSort = JSON.parse(this.commonBrowserStorageService.get(this.clientContextService.getUnit().key, this.componentIdentifier, TABLE_SORT_KEY));
    if (tableSort != null) {
      this.sortDefinition = new SortDefinition({ column: tableSort.sortColumn, direction: tableSort.sortDirection });
      this.emitSort();
      return true;
    }
    return false;
  }

  private initializeSort() {
    console.log('initializing sort');

    if (!this.restoreTableSort()) {
      let column = 'relevantStart';
      if (!this.columnsToDisplay.includes(column)) {
        column = '';
        let i = 0;
        while (i < this.columnsToDisplay.length && column === '') {
          if (this.columnsToDisplay[i] !== 'hint') {
            column = this.columnsToDisplay[i];
          }
          i += 1;
        }
      }
      if (column !== '') {
        this.sortDefinition = new SortDefinition({ column: column });
        this.emitSort();
      }
    }
  }

  private sortData(data: any[], sort: MatSort) {

    if (sort.active == null || sort.active === '') {
      console.info('no sorting due to status sort', this.sort);
      return data;
    }

    const detail = getTransportDetailById(sort.active);

    if (detail != null) {

      const attribute = detail.divergentSortAttribute != null ? detail.divergentSortAttribute : detail.attribute;

      return data.sort((a: any, b: any) => {

        if (TransportType.TRANSPORT === a.transportType && TransportType.VEHICLE === b.transportType) {
          return -1;
        }

        if (TransportType.VEHICLE === a.transportType && TransportType.TRANSPORT === b.transportType) {
          return 1;
        }

        let relevantColumns = ['start', 'relevantStart'];

        if (relevantColumns.indexOf(detail.identifier) > -1) {
          let aIsLowPriority = a.intermitted ||
            ((a.status === TransportStatus.CANCELLED || a.status === TransportStatus.COMPLETE) && a.delayedLeave);
          let bIsLowPriority = b.intermitted ||
            ((b.status === TransportStatus.CANCELLED || b.status === TransportStatus.COMPLETE) && b.delayedLeave);

          if (aIsLowPriority && !bIsLowPriority) {
            return 1;
          }

          if (bIsLowPriority && !aIsLowPriority) {
            return -1;
          }
        }

        let value1 = a[detail.identifier];
        let value2 = b[detail.identifier];

        if (detail.dataType === DataType.DICTIONARY) {
          value1 = this.translationService.translate(detail.dictionaryRoot + value1);
          value2 = this.translationService.translate(detail.dictionaryRoot + value2);
        }

        if (detail.sortEmptysToEnd) {
          if ((value1 == null || value1 === '') && (value2 == null || value2 === '')) {
            return 0;
          }
          if (value1 == null || value1 === '') {
            return +1;
          }
          if (value2 == null || value2 === '') {
            return -1;
          }
        }

        const result = this.compare(value1, value2, sort.direction !== 'desc');
        return result;

      });

    } else {
      return data;
    }

  }

  private compare(a: number | string, b: number | string, isAsc: boolean) {
    let t = 0;
    if ((a == null || a === '') && (b == null || b === '')) {
      t = 0;
    } else if (a == null || a === '') {
      t = -1;
    } else if (b == null || b === '') {
      t = 1;
    } else {
      t = (a < b ? -1 : 1);
    }
    return t * (isAsc ? 1 : -1);
  }

  private scrollSelectionIntoView() {

    if (this.autoScroll) {
      return;
    }

    this.autoScroll = true;
    let relevantElement: ElementRef<any> = null;

    try {
      if (this.tableRows?.length > 0) {
        for (const element of this.tableRows) {
          const rowId = element.nativeElement.getAttribute('data-transport-id');
          if (rowId != null && rowId === '' + this.selectedId) {
            relevantElement = element;
            break;
          }
        }
      }
    } catch (e) {
      console.error('Error when selecting row', e);
    }

    if (relevantElement != null) {
      (async () => {

        relevantElement.nativeElement.scrollIntoView({ behavior: "smooth", block: "start" });

        await new Promise(f => setTimeout(f, 2500));

        this.autoScroll = false;

      })();
    } else {
      this.autoScroll = false;
    }



  }

  handleScroll(scrollEvent: any) {
    if (this.currentTop !== scrollEvent.top && !this.autoScroll) {
      if (!this.manuallyScrolled) {
      }
      this.manuallyScrolled = true;
    }
    this.currentTop = scrollEvent.top;
  }

  emitSort() {

    console.log('emitting sort', this.sort, this.sortDefinition);
    if (this.sort == null) {
      return;
    }

    const sortState: Sort = { active: this.sortDefinition?.column, direction: this.sortDefinition?.direction };
    this.sort.active = sortState.active;
    this.sort.direction = sortState.direction;
    this.sort.sortChange.emit(sortState);
  }

}
